import Swiper from 'swiper'
import { Navigation, Scrollbar } from 'swiper/modules'
import { debounce } from 'utils/general'
import { fetchSearchResults } from 'utils/_search'

class SearchModal extends HTMLElement {
  constructor() {
    super()

    this.breakpoint = '(min-width: 990px)'
    this.searchLimit = 3
    this.localeRoot = this.getAttribute('locale-root') || '/'
    this.searchStatus = this.querySelector('.js-search-status-region')
    this.searchLoader = this.querySelector('.js-search-result-loader')
    this.resultsList = this.querySelector('.js-header-searched-products')
    this.debouncedSearch = debounce(this.searchProducts.bind(this), 500)
    this.activeOptionIndex = -1
    this.searchRequestId = 0

    // this.headerHeight = this.closest('.header-wrapper').offsetHeight || 0
    this.screenHeight = window.innerHeight

    this.searchTimeOut = ''

    this.addEventListener('resetSearchResult', () => this.resetResult())
  }

  connectedCallback() {
    if (this.formInputInitialized) return
    this.formInputInitialized = true
    this.handleFormInput()
  }

  get searchInput() {
    this.searchInputElement ??=
      this.querySelector('.js-search-input') ||
      this.closest('hh-details-modal')?.querySelector('.js-search-input')
    return this.searchInputElement
  }

  handleFormInput() {
    this.searchInput.addEventListener('keydown', event => this.onInputKeydown(event))
    this.searchInput.addEventListener('keyup', (e) => {
      if (e.key === 'Enter') {
        if (!this.searchInput.value.trim()) {
          e.preventDefault()
          e.stopPropagation()
          this.closest('hh-details-modal')?.toggle(false, false)
        }
        return
      }

      if (['ArrowDown', 'ArrowUp', 'Escape'].includes(e.key)) return

      this.changeLoadingState(true)
      this.debouncedSearch()
    })
  }

  get options() {
    return [...this.resultsList.querySelectorAll('[role="option"]')]
  }

  clearActiveOption() {
    this.options.forEach(option => option.setAttribute('aria-selected', 'false'))
    this.activeOptionIndex = -1
    this.searchInput.removeAttribute('aria-activedescendant')
  }

  setResultsVisibility(visible) {
    this.classList.toggle('show-result', visible)
    this.searchInput.setAttribute('aria-expanded', String(visible))
    if (!visible) this.clearActiveOption()
  }

  setActiveOption(index) {
    const options = this.options
    if (!options.length) return

    this.activeOptionIndex = (index + options.length) % options.length
    options.forEach((option, optionIndex) => {
      option.setAttribute('aria-selected', String(optionIndex === this.activeOptionIndex))
    })

    const activeOption = options[this.activeOptionIndex]
    this.searchInput.setAttribute('aria-activedescendant', activeOption.id)
    activeOption.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }

  onInputKeydown(event) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (!this.options.length) return
      event.preventDefault()
      const direction = event.key === 'ArrowDown' ? 1 : -1
      const nextIndex = this.activeOptionIndex < 0
        ? direction > 0 ? 0 : this.options.length - 1
        : this.activeOptionIndex + direction
      this.setActiveOption(nextIndex)
      return
    }

    if (event.key === 'Enter' && this.activeOptionIndex >= 0) {
      const activeLink = this.options[this.activeOptionIndex]?.querySelector('a[href]')
      if (!activeLink) return
      event.preventDefault()
      activeLink.click()
      return
    }

    if (event.key === 'Enter') {
      event.preventDefault()

      if (!this.searchInput.value.trim()) {
        event.stopPropagation()
        return
      }

      const form = this.searchInput.form
      const url = new URL(form.action, window.location.origin)
      url.search = new URLSearchParams(new FormData(form)).toString()
      window.location.assign(url)
      return
    }

    if (event.key === 'Escape' && this.searchInput.getAttribute('aria-expanded') === 'true') {
      event.preventDefault()
      event.stopPropagation()
      this.searchRequestId++
      this.changeLoadingState(false)
      this.setResultsVisibility(false)
      this.searchInput.focus()
    }
  }

  initSlider() {
    const slider = this.querySelector('[data-search-result-slider]')
    if (!slider) return
    new Swiper(slider, {
      modules: [Navigation, Scrollbar],
      direction: 'horizontal',
      autoHeight: false,
      spaceBetween: 8,
      slidesPerView: 1,
      draggable: true,
      breakpoints: {
        480: {
          slidesPerView: 3,
        },
        900: {
          slidesPerView: 6,
        },
        990: {
          autoHeight: true,
          slidesPerView: 6,
        },
      },
      scrollbar: {
        el: '.swiper-scrollbar',
        draggable: true,
        hide: false,
      },
      navigation: {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
      },
    })
  }

  isMobile() {
    return !window.matchMedia(this.breakpoint).matches
  }

  changeLoadingState(loading = false) {
    this.searchLoader.setAttribute('aria-busy', String(loading))
    loading
      ? this.searchLoader.classList.add('is-loading')
      : this.searchLoader.classList.remove('is-loading')
  }

  resetResult() {
    this.searchRequestId++
    this.querySelector('.js-header-searched-products').innerHTML = ''
    this.searchInput.value = ''
    this.setResultsVisibility(false)
    this.changeLoadingState(false)
    this.searchStatus.textContent = ''
  }

  localizeProductCardLinks(productCard) {
    if (this.localeRoot === '/') return

    productCard.querySelectorAll('a[href]').forEach(link => {
      const url = new URL(link.getAttribute('href'), window.location.origin)
      if (url.origin !== window.location.origin || !url.pathname.startsWith('/products/')) return

      link.setAttribute(
        'href',
        `${this.localeRoot.replace(/\/$/, '')}${url.pathname}${url.search}${url.hash}`
      )
    })
  }

  async searchProducts() {
    const query = this.searchInput?.value.trim()
    if (!query) {
      this.setResultsVisibility(false)
      this.changeLoadingState(false)
      return
    }

    const requestId = ++this.searchRequestId
    const html = await fetchSearchResults(query)
    if (requestId !== this.searchRequestId || query !== this.searchInput?.value.trim()) return

    this.searchStatus.textContent =
      html?.querySelector('.js-searched-status')?.textContent.trim() || ''

    this.searchLimit = 8
    this.resultsList.innerHTML = ''

    if (this.closest('hh-details-modal').isMobile()) {
      this.querySelector('.js-header-searched-products').style.height = `${
        this.screenHeight -
        this.getBoundingClientRect().height -
        this.querySelector('.js-header-searched-products').clientHeight +
        64 // Static height of loader
      }px`
    }
    const results = []
    html?.querySelectorAll('.js-searched-products > li').forEach((element) => {
      this.searchLimit--

      element.classList.remove('gfi')
      element.classList.remove('mb1')
      element.querySelector('.size__options__wrapper')?.remove()
      element.querySelector('.card__options')?.remove()
      element.querySelector('.product__grid__sibs')?.remove()
      element.querySelector('.more-colors')?.remove()
      element.querySelector('hh-wishlist-button')?.remove()
      this.localizeProductCardLinks(element)

      element.classList.add('bg-reverse', 'swiper-slide')
      element.setAttribute('role', 'option')
      element.setAttribute('aria-selected', 'false')
      element.id = `${this.resultsList.id}-Option-${results.length + 1}`
      if (this.searchLimit >= 0) results.push(element)
    })

    this.resultsList.replaceChildren(...results)
    this.clearActiveOption()
    this.setResultsVisibility(results.length > 0)

    setTimeout(() => {
      this.initSlider()
      this.changeLoadingState(false)
    })
  }
}

customElements.define('search-modal', SearchModal)
